<template>
    <!-- 管理员-项目管理/阶段管理 -->
    <div class="stage-manage wrapper" v-loading="loading">
        <proTitle></proTitle>
        <div class="toolbar">
            <el-button type="primary" size="small" icon="el-icon-plus" @click="openCreate">新增阶段</el-button>
        </div>

        <div class="phase-list">
            <div class="phase-card" :class="{ 'is-disabled': !phase.enabled }" v-for="phase in phaseList"
                :key="phase.id">
                <div class="phase-card-head flex-align-between">
                    <div class="head-left flex-align">
                        <i class="el-icon-location pin-icon"></i>
                        <span class="phase-title">{{ phase.displayName }}</span>
                        <el-tag size="mini" :type="phase.enabled ? 'success' : 'info'">
                            {{ phase.enabled ? '已启用' : '已停用' }}
                        </el-tag>
                    </div>
                    <div class="head-actions">
                        <el-button size="mini" plain :loading="phase._toggling" @click="toggleEnable(phase)">
                            {{ phase.enabled ? '取消启用' : '启用' }}
                        </el-button>
                        <el-button size="mini" plain @click="openEdit(phase)">编辑阶段</el-button>
                        <el-button size="mini" type="danger" plain @click="deletePhase(phase)">删除</el-button>
                    </div>
                </div>
                <div class="phase-meta">
                    <p class="meta-line">用户组：{{ phase.userGroupNames.join('、') || '—' }}</p>
                    <template v-if="phase.periods.length">
                        <template v-for="(period, index) in phase.periods">
                            <p class="meta-line" :key="'week' + phase.id + '-' + index">
                                {{ weekLabel(index) }}：{{ period.start }} ~ {{ period.end }}
                            </p>
                            <p class="meta-line is-muted" :key="'attend' + phase.id + '-' + index">
                                考勤：{{ weekAttendanceSummary(phase, period) }}
                            </p>
                        </template>
                    </template>
                    <template v-else>
                        <p class="meta-line is-muted">时间：—</p>
                        <p class="meta-line is-muted">考勤：未配置</p>
                    </template>
                </div>
                <div class="phase-created">创建时间：{{ phase.createdAt || '—' }}</div>
            </div>
        </div>
        <el-empty v-if="!loading && !phaseList.length" description="暂无阶段，请点击「新增阶段」创建"></el-empty>

        <!-- 新增/编辑阶段 -->
        <el-dialog :title="isCreating ? '新增阶段' : '编辑阶段'" :visible.sync="dialogVisible" width="820px" top="6vh"
            custom-class="stage-manage-dialog" :close-on-click-modal="false">
            <el-form label-width="90px" size="small" @submit.native.prevent>
                <el-form-item label="阶段名称" required>
                    <el-select v-model="form.type" style="width:170px" @change="phaseTypeChange">
                        <el-option v-for="opt in phaseOptions" :key="opt.type" :label="opt.label"
                            :value="opt.type"></el-option>
                    </el-select>
                    <el-input v-model.trim="form.displayName" placeholder="如：集中培训" style="width:200px;margin-left:10px"
                        maxlength="20"></el-input>
                </el-form-item>
                <el-form-item label="用户组" required>
                    <el-checkbox v-if="userGroupOptions.length" class="user-group-check-all"
                        :indeterminate="userGroupIndeterminate" v-model="userGroupCheckAll">全选</el-checkbox>
                    <el-checkbox-group v-model="form.userGroupIds" class="user-group-checkboxes">
                        <el-checkbox v-for="group in userGroupOptions" :key="group.id" :label="group.id">
                            <span class="user-group-label" :title="group.name">{{ group.name }}</span>
                        </el-checkbox>
                    </el-checkbox-group>
                    <div class="form-hint" v-if="userGroupOptions.length">选择本阶段适用的用户组，可多选</div>
                    <div class="form-hint" v-else>当前项目暂无可用用户组，请先在项目中配置</div>
                </el-form-item>
                <el-form-item label="阶段周次">
                    <div class="week-row" v-for="(week, index) in form.weeks" :key="index">
                        <span class="week-label">{{ weekLabel(index) }}</span>
                        <el-date-picker v-model="week.start" type="date" value-format="yyyy-MM-dd"
                            placeholder="开始日期" :picker-options="weekPickerOptions(index, 'start')"
                            style="width:150px">
                        </el-date-picker>
                        <span class="date-range-sep">至</span>
                        <el-date-picker v-model="week.end" type="date" value-format="yyyy-MM-dd"
                            placeholder="结束日期" :picker-options="weekPickerOptions(index, 'end')"
                            style="width:150px">
                        </el-date-picker>
                        <el-button type="text" class="btn-remove-week" icon="el-icon-close"
                            @click="removeWeek(index)"></el-button>
                    </div>
                    <el-button type="text" icon="el-icon-plus" @click="addWeek">添加一周</el-button>
                    <div class="form-hint">可选范围：项目执行期 {{ projectStart || '—' }} ~ {{ projectEnd || '—'
                        }}；已被其他阶段或其他周占用的日期不可选
                    </div>
                </el-form-item>
                <el-form-item label="考勤配置">
                    <el-button type="text" @click="generateAttendance">根据周次生成</el-button>
                    <div class="form-hint" style="margin-top:0">
                        考勤分为两类：上午为「{{ attendanceName }}考勤」，晚上为「住宿考勤」。在日历上点选或滑动拖选多日，各类别统一设置打卡时间段（可不配置）
                    </div>
                    <div class="attendance-categories">
                        <div class="attendance-category" :class="type" v-for="type in ['morning', 'evening']"
                            :key="type">
                            <div class="attendance-category-head">
                                <span class="attendance-category-title">
                                    {{ type === 'morning' ? `上午 · ${attendanceName}考勤` : '晚上 · 住宿考勤' }}
                                </span>
                            </div>
                            <div class="attendance-category-body">
                                <div class="attendance-time-row flex-align">
                                    <span class="attendance-time-label">打卡时间段</span>
                                    <div class="attendance-time-range flex-align">
                                        <el-time-picker v-model="form.attendance[type].timeStart" value-format="HH:mm"
                                            format="HH:mm" placeholder="开始" style="width:110px"></el-time-picker>
                                        <span class="attendance-time-sep">至</span>
                                        <el-time-picker v-model="form.attendance[type].timeEnd" value-format="HH:mm"
                                            format="HH:mm" placeholder="结束" style="width:110px"></el-time-picker>
                                    </div>
                                    <span class="attendance-selected-count">已选 {{ form.attendance[type].dates.length }}
                                        天</span>
                                </div>
                                <div class="attendance-calendar">
                                    <div class="calendar-nav">
                                        <button type="button" class="calendar-nav-btn"
                                            :disabled="!canGoAttendanceMonth(type, -1)"
                                            @click="shiftAttendanceMonth(type, -1)">‹</button>
                                        <span class="calendar-title">{{ attendanceCalendarTitle(type) }}</span>
                                        <button type="button" class="calendar-nav-btn"
                                            :disabled="!canGoAttendanceMonth(type, 1)"
                                            @click="shiftAttendanceMonth(type, 1)">›</button>
                                    </div>
                                    <div class="calendar-weekdays">
                                        <span class="calendar-weekday" v-for="label in weekdayLabels" :key="label">
                                            {{ label }}
                                        </span>
                                    </div>
                                    <div class="calendar-days"
                                        :class="{ 'is-dragging': calendarDrag.type === type && calendarDrag.active }"
                                        @mousedown="onAttendanceCalendarMouseDown($event, type)"
                                        @mouseover="onAttendanceCalendarMouseOver($event, type)"
                                        @mouseup="onAttendanceCalendarMouseUp($event, type)">
                                        <button type="button" class="calendar-day"
                                            v-for="(cell, idx) in getAttendanceCalendarCells(type)" :key="idx" :class="{
                                                'is-empty': cell.empty,
                                                'is-disabled': cell.disabled,
                                                'is-selected': cell.selected,
                                                'is-in-range': cell.inRange,
                                                'is-deselect-preview': cell.deselectPreview,
                                            }" :disabled="cell.empty || cell.disabled"
                                            :data-date="cell.empty ? null : cell.date" tabindex="-1">{{ cell.empty ? ''
                                                :
                                                cell.day }}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button size="small" @click="dialogVisible = false">取消</el-button>
                <el-button size="small" type="primary" :loading="saving" @click="savePhase">保存</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import { mapState } from 'vuex';

    const PHASE_OPTIONS = [
        { type: 1, label: '阶段一-集中', displayName: '集中培训' },
        { type: 2, label: '阶段二-跟岗', displayName: '跟岗学习' },
        { type: 3, label: '阶段三-返岗', displayName: '返岗实践' },
    ];

    const datesInRange = (start, end) => {
        let dates = [];
        let current = new Date(start.replace(/-/g, '/'));
        let last = new Date(end.replace(/-/g, '/'));
        while (current <= last) {
            let m = String(current.getMonth() + 1).padStart(2, '0');
            let d = String(current.getDate()).padStart(2, '0');
            dates.push(`${current.getFullYear()}-${m}-${d}`);
            current.setDate(current.getDate() + 1);
        }
        return dates;
    };

    const toDateStr = (val) => {
        if (!val) return '';
        if (typeof val === 'string') return val.slice(0, 10);
        let d = new Date(val);
        if (Number.isNaN(d.getTime())) return '';
        let m = String(d.getMonth() + 1).padStart(2, '0');
        let day = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${m}-${day}`;
    };

    // put 返回完整 axios response，get/post 返回 body
    const unwrapApi = (res) => {
        if (!res) return res;
        if (res.status != null && res.data && typeof res.data === 'object' && 'code' in res.data) {
            return res.data;
        }
        return res;
    };

    const isApiSuccess = (res) => {
        let body = unwrapApi(res);
        return body && (body.code === 0 || body.code === 200);
    };

    const apiMsg = (res) => {
        let body = unwrapApi(res);
        return (body && body.msg) || '';
    };

    const emptyAttendance = () => ({
        morning: { timeStart: '08:00', timeEnd: '09:30', dates: [] },
        evening: { timeStart: '20:30', timeEnd: '22:00', dates: [] },
    });

    export default {
        data() {
            return {
                loading: false,
                saving: false,
                projectStart: '',
                projectEnd: '',
                phaseOptions: PHASE_OPTIONS,
                userGroupOptions: [],
                phaseList: [],
                dialogVisible: false,
                isCreating: false,
                editingId: null,
                lastType: null,
                weekdayLabels: ['日', '一', '二', '三', '四', '五', '六'],
                attendanceCalendarMonths: {
                    morning: { year: new Date().getFullYear(), month: new Date().getMonth() },
                    evening: { year: new Date().getFullYear(), month: new Date().getMonth() },
                },
                calendarDrag: {
                    type: null,
                    active: false,
                    moved: false,
                    mode: 'select',
                    anchor: null,
                    lastTarget: null,
                },
                calendarDragPreview: [],
                form: {
                    type: 1,
                    displayName: '',
                    userGroupIds: [],
                    weeks: [],
                    attendance: emptyAttendance(),
                },
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            projectId() {
                return this.currentProject && this.currentProject.id;
            },
            attendanceName() {
                if (this.form.displayName) return this.form.displayName;
                let opt = this.phaseOptions.find(item => item.type === this.form.type);
                return opt ? opt.displayName : '阶段';
            },
            userGroupCheckAll: {
                get() {
                    const total = this.userGroupOptions.length
                    return total > 0 && this.form.userGroupIds.length === total
                },
                set(val) {
                    this.form.userGroupIds = val ? this.userGroupOptions.map(g => g.id) : []
                },
            },
            userGroupIndeterminate() {
                const checked = this.form.userGroupIds.length
                return checked > 0 && checked < this.userGroupOptions.length
            },
            attendanceAllowedDates() {
                let allowedDates = {};
                this.form.weeks.forEach(week => {
                    if (week.start && week.end) {
                        datesInRange(week.start, week.end).forEach(d => {
                            allowedDates[d] = true;
                        });
                    }
                });
                return allowedDates;
            },
        },
        watch: {
            projectId: {
                immediate: true,
                handler(id) {
                    if (id) this.loadAll();
                    else {
                        this.phaseList = [];
                        this.userGroupOptions = [];
                    }
                },
            },
        },
        mounted() {
            this._onDocMouseUp = () => this.endAttendanceCalendarDrag();
            document.addEventListener('mouseup', this._onDocMouseUp);
        },
        beforeDestroy() {
            document.removeEventListener('mouseup', this._onDocMouseUp);
        },
        methods: {
            weekLabel(index) {
                const cn = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];
                return `第${cn[index] || index + 1}周`;
            },
            isAttendanceDateSelectable(dateStr) {
                return !!this.attendanceAllowedDates[dateStr];
            },
            attendanceCalendarTitle(type) {
                let monthInfo = this.attendanceCalendarMonths[type];
                return `${monthInfo.year}年${monthInfo.month + 1}月`;
            },
            canGoAttendanceMonth(type, step) {
                let monthInfo = this.attendanceCalendarMonths[type];
                let year = monthInfo.year;
                let month = monthInfo.month + step;
                if (month < 0) {
                    year -= 1;
                    month = 11;
                } else if (month > 11) {
                    year += 1;
                    month = 0;
                }
                if (step < 0) {
                    if (!this.projectStart) return true;
                    let lastDayPrev = toDateStr(new Date(year, month + 1, 0));
                    return lastDayPrev >= this.projectStart;
                }
                if (!this.projectEnd) return true;
                let firstDayNext = toDateStr(new Date(year, month, 1));
                return firstDayNext <= this.projectEnd;
            },
            shiftAttendanceMonth(type, step) {
                if (!this.canGoAttendanceMonth(type, step)) return;
                let monthInfo = this.attendanceCalendarMonths[type];
                let year = monthInfo.year;
                let month = monthInfo.month + step;
                if (month < 0) {
                    year -= 1;
                    month = 11;
                } else if (month > 11) {
                    year += 1;
                    month = 0;
                }
                this.$set(this.attendanceCalendarMonths, type, { year, month });
            },
            initAttendanceCalendarMonths() {
                ['morning', 'evening'].forEach(type => {
                    let dates = this.form.attendance[type].dates || [];
                    let base = dates[0];
                    if (!base) {
                        let allowed = Object.keys(this.attendanceAllowedDates).sort();
                        base = allowed[0] || this.projectStart || toDateStr(new Date());
                    }
                    let parts = base.split('-');
                    this.$set(this.attendanceCalendarMonths, type, {
                        year: +parts[0],
                        month: +parts[1] - 1,
                    });
                });
            },
            getAttendanceCalendarCells(type) {
                let monthInfo = this.attendanceCalendarMonths[type];
                let year = monthInfo.year;
                let month = monthInfo.month;
                let firstDay = new Date(year, month, 1);
                let startWeekday = firstDay.getDay();
                let daysInMonth = new Date(year, month + 1, 0).getDate();
                let selectedSet = {};
                (this.form.attendance[type].dates || []).forEach(date => {
                    selectedSet[date] = true;
                });
                let previewSet = {};
                let drag = this.calendarDrag;
                let isDeselectPreview = drag.type === type && drag.active && drag.mode === 'deselect';
                if (drag.type === type) {
                    this.calendarDragPreview.forEach(date => {
                        previewSet[date] = true;
                    });
                }
                let cells = [];
                for (let i = 0; i < startWeekday; i++) {
                    cells.push({
                        empty: true, date: '', day: '', disabled: true,
                        selected: false, inRange: false, deselectPreview: false,
                    });
                }
                for (let day = 1; day <= daysInMonth; day++) {
                    let date = toDateStr(new Date(year, month, day));
                    let disabled = !this.isAttendanceDateSelectable(date);
                    let selected = !!selectedSet[date];
                    let inPreview = !!previewSet[date];
                    cells.push({
                        empty: false,
                        date,
                        day,
                        disabled,
                        selected: selected && !(isDeselectPreview && inPreview),
                        inRange: !disabled && !selected && !isDeselectPreview && inPreview,
                        deselectPreview: !disabled && selected && isDeselectPreview && inPreview,
                    });
                }
                return cells;
            },
            getDatesBetweenSelectable(dateA, dateB) {
                if (!dateA || !dateB) return [];
                let start = dateA <= dateB ? dateA : dateB;
                let end = dateA <= dateB ? dateB : dateA;
                return datesInRange(start, end).filter(date => this.isAttendanceDateSelectable(date));
            },
            setAttendanceDragPreview(anchor, target) {
                this.calendarDragPreview = this.getDatesBetweenSelectable(anchor, target);
            },
            applyAttendanceDragRange(type, anchor, target, mode) {
                let dates = this.getDatesBetweenSelectable(anchor, target);
                if (!dates.length) return;
                let selectedSet = {};
                (this.form.attendance[type].dates || []).forEach(date => {
                    selectedSet[date] = true;
                });
                if (mode === 'deselect') {
                    dates.forEach(date => {
                        delete selectedSet[date];
                    });
                } else {
                    dates.forEach(date => {
                        selectedSet[date] = true;
                    });
                }
                this.form.attendance[type].dates = Object.keys(selectedSet).sort();
            },
            toggleAttendanceDate(type, date) {
                if (!this.isAttendanceDateSelectable(date)) return;
                let dates = this.form.attendance[type].dates || [];
                let idx = dates.indexOf(date);
                if (idx === -1) dates.push(date);
                else dates.splice(idx, 1);
                this.form.attendance[type].dates = dates.slice().sort();
            },
            resetAttendanceCalendarDrag() {
                this.calendarDrag = {
                    type: null,
                    active: false,
                    moved: false,
                    mode: 'select',
                    anchor: null,
                    lastTarget: null,
                };
                this.calendarDragPreview = [];
            },
            endAttendanceCalendarDrag() {
                let drag = this.calendarDrag;
                if (!drag.active || !drag.type) return;
                if (drag.moved && drag.anchor && drag.lastTarget) {
                    this.applyAttendanceDragRange(drag.type, drag.anchor, drag.lastTarget, drag.mode);
                }
                this.resetAttendanceCalendarDrag();
            },
            onAttendanceCalendarMouseDown(e, type) {
                if (e.button !== 0) return;
                let target = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
                let btn = target && target.closest && target.closest('.calendar-day[data-date]');
                if (!btn || btn.disabled) return;
                e.preventDefault();
                let date = btn.getAttribute('data-date');
                if (!date) return;
                let isSelected = (this.form.attendance[type].dates || []).indexOf(date) !== -1;
                this.calendarDrag = {
                    type,
                    active: true,
                    moved: false,
                    mode: isSelected ? 'deselect' : 'select',
                    anchor: date,
                    lastTarget: date,
                };
                this.setAttendanceDragPreview(date, date);
            },
            onAttendanceCalendarMouseOver(e, type) {
                let drag = this.calendarDrag;
                if (!drag.active || drag.type !== type || !drag.anchor) return;
                let target = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
                let btn = target && target.closest && target.closest('.calendar-day[data-date]');
                if (!btn || btn.disabled) return;
                let date = btn.getAttribute('data-date');
                if (!date) return;
                if (date !== drag.anchor) drag.moved = true;
                drag.lastTarget = date;
                this.setAttendanceDragPreview(drag.anchor, date);
            },
            onAttendanceCalendarMouseUp(e, type) {
                let drag = this.calendarDrag;
                if (!drag.active || drag.type !== type || !drag.anchor) return;
                let target = e.target && e.target.nodeType === 1 ? e.target : (e.target && e.target.parentElement);
                let btn = target && target.closest && target.closest('.calendar-day[data-date]');
                let targetDate = btn && !btn.disabled ? btn.getAttribute('data-date') : drag.anchor;
                if (drag.moved) {
                    this.applyAttendanceDragRange(type, drag.anchor, targetDate, drag.mode);
                } else if (btn && !btn.disabled && targetDate) {
                    this.toggleAttendanceDate(type, targetDate);
                }
                this.resetAttendanceCalendarDrag();
            },
            weekPickerOptions(currentIndex, bound) {
                let start = this.projectStart;
                let end = this.projectEnd;
                let currentWeek = this.form.weeks[currentIndex] || {};
                let occupied = [];
                // 本阶段其他周已占用
                this.form.weeks.forEach((week, i) => {
                    if (i === currentIndex) return;
                    if (week.start && week.end) {
                        occupied.push({ start: week.start, end: week.end });
                    }
                });
                // 其他阶段已占用（编辑时排除当前阶段）
                this.phaseList.forEach(phase => {
                    if (this.editingId && phase.id === this.editingId) return;
                    (phase.periods || []).forEach(period => {
                        if (period.start && period.end) {
                            occupied.push({ start: period.start, end: period.end });
                        }
                    });
                });
                return {
                    disabledDate(date) {
                        let m = String(date.getMonth() + 1).padStart(2, '0');
                        let d = String(date.getDate()).padStart(2, '0');
                        let str = `${date.getFullYear()}-${m}-${d}`;
                        if (start && end && (str < start || str > end)) return true;
                        if (bound === 'start' && currentWeek.end && str > currentWeek.end) return true;
                        if (bound === 'end' && currentWeek.start && str < currentWeek.start) return true;
                        for (let range of occupied) {
                            if (str >= range.start && str <= range.end) return true;
                        }
                        return false;
                    },
                };
            },
            weekAttendanceSummary(phase, period) {
                let weekDates = datesInRange(period.start, period.end);
                let daySet = {};
                let times = 0;
                ['morning', 'evening'].forEach(type => {
                    (phase.attendance[type].dates || []).forEach(date => {
                        if (weekDates.indexOf(date) === -1) return;
                        daySet[date] = true;
                        times += 1;
                    });
                });
                let dayCount = Object.keys(daySet).length;
                return times ? `${dayCount}天·${times}次` : '未配置';
            },
            defaultDisplayName(type) {
                let opt = this.phaseOptions.find(item => item.type === type);
                return opt ? opt.displayName : '';
            },
            mapStageItem(vo) {
                let groups = Array.isArray(vo.userGroups) ? vo.userGroups : [];
                let userGroupIds = Array.isArray(vo.userGroupIds) && vo.userGroupIds.length
                    ? vo.userGroupIds.map(Number)
                    : groups.map(g => Number(g.id));
                let periods = (vo.periods || []).map(p => ({
                    cycleId: p.cycleId,
                    cycleNumber: p.cycleNumber,
                    cycleName: p.cycleName,
                    start: p.startDate,
                    end: p.endDate,
                }));
                return {
                    id: vo.id,
                    type: Number(vo.type),
                    typeLabel: vo.typeLabel || '',
                    displayName: vo.stageName || '',
                    enabled: Number(vo.enabled) === 1,
                    createdAt: toDateStr(vo.createdAt),
                    userGroupIds,
                    userGroupNames: groups.map(g => g.name).filter(Boolean),
                    periods,
                    attendance: {
                        morning: {
                            timeStart: vo.morningStart || '08:00',
                            timeEnd: vo.morningEnd || '09:30',
                            dates: (vo.morningDates || []).slice(),
                        },
                        evening: {
                            timeStart: vo.stayStart || '20:30',
                            timeEnd: vo.stayEnd || '22:00',
                            dates: (vo.stayDates || []).slice(),
                        },
                    },
                    trackDates: (vo.trackDates || []).slice(),
                };
            },
            async loadAll() {
                if (!this.projectId) return;
                this.loading = true;
                try {
                    await Promise.all([this.fetchProjectInfo(), this.fetchStageList()]);
                } finally {
                    this.loading = false;
                }
            },
            async fetchProjectInfo() {
                let res = await this.$Api.StageV2.getProjectInfo({ projectId: this.projectId });
                if (!isApiSuccess(res)) {
                    this.$message.error(apiMsg(res) || '获取项目信息失败');
                    return;
                }
                let data = res.data || {};
                this.projectStart = toDateStr(data.periodStartTime);
                this.projectEnd = toDateStr(data.periodFinishTime);
                this.userGroupOptions = (data.availableUserGroups || []).map(g => ({
                    id: Number(g.id),
                    name: g.name,
                }));
            },
            async fetchStageList() {
                let res = await this.$Api.StageV2.getStageList({ projectId: this.projectId });
                if (!isApiSuccess(res)) {
                    this.$message.error(apiMsg(res) || '获取阶段列表失败');
                    this.phaseList = [];
                    return;
                }
                this.phaseList = (res.data || []).map(item => this.mapStageItem(item));
            },
            phaseTypeChange(value) {
                if (!this.form.displayName || this.form.displayName === this.defaultDisplayName(this.lastType)) {
                    this.form.displayName = this.defaultDisplayName(value);
                }
                this.lastType = value;
            },
            addWeek() {
                this.form.weeks.push({ start: '', end: '', cycleId: null });
            },
            removeWeek(index) {
                if (this.form.weeks.length <= 1) {
                    this.$message.warning('至少保留一周');
                    return;
                }
                this.form.weeks.splice(index, 1);
            },
            generateAttendance() {
                let weeks = this.form.weeks.filter(week => week.start && week.end);
                if (!weeks.length) {
                    this.$message.warning('请先完整配置阶段周次，再生成考勤日期');
                    return;
                }
                let dateMap = {};
                weeks.forEach(week => {
                    datesInRange(week.start, week.end).forEach(date => (dateMap[date] = true));
                });
                let dates = Object.keys(dateMap).sort();
                this.form.attendance.morning.dates = dates.slice();
                this.form.attendance.evening.dates = dates.slice();
                this.initAttendanceCalendarMonths();
            },
            openCreate() {
                this.isCreating = true;
                this.editingId = null;
                let type = 1;
                this.form = {
                    type,
                    displayName: this.defaultDisplayName(type),
                    userGroupIds: this.userGroupOptions.map(g => g.id),
                    weeks: [{ start: '', end: '', cycleId: null }],
                    attendance: emptyAttendance(),
                };
                this.lastType = type;
                this.resetAttendanceCalendarDrag();
                this.initAttendanceCalendarMonths();
                this.dialogVisible = true;
            },
            openEdit(phase) {
                this.isCreating = false;
                this.editingId = phase.id;
                this.form = {
                    type: phase.type,
                    displayName: phase.displayName,
                    userGroupIds: phase.userGroupIds.slice(),
                    weeks: phase.periods.length
                        ? phase.periods.map(period => ({
                            start: period.start || '',
                            end: period.end || '',
                            cycleId: period.cycleId || null,
                        }))
                        : [{ start: '', end: '', cycleId: null }],
                    attendance: {
                        morning: {
                            timeStart: phase.attendance.morning.timeStart,
                            timeEnd: phase.attendance.morning.timeEnd,
                            dates: phase.attendance.morning.dates.slice(),
                        },
                        evening: {
                            timeStart: phase.attendance.evening.timeStart,
                            timeEnd: phase.attendance.evening.timeEnd,
                            dates: phase.attendance.evening.dates.slice(),
                        },
                    },
                };
                this.lastType = phase.type;
                this.resetAttendanceCalendarDrag();
                this.initAttendanceCalendarMonths();
                this.dialogVisible = true;
            },
            buildSavePayload() {
                let periods = [];
                for (let i = 0; i < this.form.weeks.length; i++) {
                    let week = this.form.weeks[i];
                    if (!week.start || !week.end) {
                        this.$message.warning(`请完整填写${this.weekLabel(i)}的起止日期`);
                        return null;
                    }
                    if (week.start > week.end) {
                        this.$message.warning(`${this.weekLabel(i)}的开始日期不能晚于结束日期`);
                        return null;
                    }
                    // 本阶段周次之间不可重叠
                    for (let j = 0; j < i; j++) {
                        let prev = this.form.weeks[j];
                        if (prev.start && prev.end && week.start <= prev.end && week.end >= prev.start) {
                            this.$message.warning(`${this.weekLabel(i)}与${this.weekLabel(j)}的日期存在重叠`);
                            return null;
                        }
                    }
                    // 不可与其他阶段已选日期重叠
                    for (let phase of this.phaseList) {
                        if (this.editingId && phase.id === this.editingId) continue;
                        for (let period of (phase.periods || [])) {
                            if (!period.start || !period.end) continue;
                            if (week.start <= period.end && week.end >= period.start) {
                                this.$message.warning(`${this.weekLabel(i)}与「${phase.displayName}」的日期存在重叠`);
                                return null;
                            }
                        }
                    }
                    let item = {
                        cycleNumber: i + 1,
                        cycleName: this.weekLabel(i),
                        startDate: week.start,
                        endDate: week.end,
                    };
                    if (week.cycleId) item.cycleId = week.cycleId;
                    periods.push(item);
                }
                let morning = this.form.attendance.morning;
                let evening = this.form.attendance.evening;
                return {
                    id: this.isCreating ? null : this.editingId,
                    enabled: this.isCreating ? 0 : undefined,
                    projectId: this.projectId,
                    stageName: this.form.displayName,
                    type: this.form.type,
                    userGroupIds: this.form.userGroupIds.map(Number),
                    morningStart: morning.timeStart || null,
                    morningEnd: morning.timeEnd || null,
                    stayStart: evening.timeStart || null,
                    stayEnd: evening.timeEnd || null,
                    periods,
                    morningDates: (morning.dates || []).slice().sort(),
                    stayDates: (evening.dates || []).slice().sort(),
                    trackDates: [],
                };
            },
            async savePhase() {
                if (!this.form.displayName) {
                    this.$message.warning('请填写阶段名称');
                    return;
                }
                if (!this.form.userGroupIds.length) {
                    this.$message.warning('请至少选择一个用户组');
                    return;
                }
                if (!this.projectId) {
                    this.$message.warning('未获取到当前项目');
                    return;
                }
                let payload = this.buildSavePayload();
                if (!payload) return;
                this.saving = true;
                try {
                    let res = await this.$Api.StageV2.saveStage(payload);
                    if (isApiSuccess(res)) {
                        this.$message.success('保存成功');
                        this.dialogVisible = false;
                        await this.fetchStageList();
                    } else {
                        this.$message.error(apiMsg(res) || '保存失败');
                    }
                } catch (e) {
                    this.$message.error('保存失败');
                } finally {
                    this.saving = false;
                }
            },
            async toggleEnable(phase) {
                let next = phase.enabled ? 0 : 1;
                this.$set(phase, '_toggling', true);
                try {
                    let res = await this.$Api.StageV2.setStageEnabled({
                        stageId: phase.id,
                        enabled: next,
                    });
                    if (isApiSuccess(res)) {
                        phase.enabled = next === 1;
                        this.$message.success(next === 1 ? '已启用' : '已取消启用');
                    } else {
                        this.$message.error(apiMsg(res) || '操作失败');
                    }
                } catch (e) {
                    this.$message.error('操作失败');
                } finally {
                    this.$set(phase, '_toggling', false);
                }
            },
            deletePhase(phase) {
                this.$confirm(`确定删除「${phase.displayName}」？`, '提示', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning',
                }).then(async () => {
                    try {
                        let res = await this.$Api.StageV2.deleteStage({ stageId: phase.id });
                        if (isApiSuccess(res)) {
                            this.$message.success('删除成功');
                            await this.fetchStageList();
                        } else {
                            this.$message.error(apiMsg(res) || '删除失败');
                        }
                    } catch (e) {
                        this.$message.error('删除失败');
                    }
                }).catch(() => { });
            },
        },
    };
</script>

<style lang="less" scoped>
    .stage-manage {
        padding: 20px 25px;
        box-sizing: border-box;

        .toolbar {
            margin-bottom: 16px;
        }

        .phase-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .phase-card {
            position: relative;
            background: #fff;
            border: 1px solid #93C5FD;
            border-radius: 10px;
            padding: 16px 18px 36px;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

            &.is-disabled {
                opacity: 0.72;
                border-color: #D1D5DB;
                background: #FAFAFA;
            }

            .head-left {
                .pin-icon {
                    color: #EC4899;
                    margin-right: 8px;
                }

                .phase-title {
                    font-size: 15px;
                    font-weight: 600;
                    color: #303133;
                    margin-right: 8px;
                }
            }

            .phase-meta {
                margin-top: 10px;

                .meta-line {
                    font-size: 13px;
                    color: #606266;
                    line-height: 1.9;

                    &.is-muted {
                        color: #909399;
                    }
                }
            }

            .phase-created {
                position: absolute;
                right: 18px;
                bottom: 12px;
                font-size: 12px;
                color: #C0C4CC;
            }
        }

        .user-group-check-all {
            display: block;
            margin-bottom: 6px;
        }

        .user-group-checkboxes {
            .user-group-label {
                display: inline-block;
                max-width: 160px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                vertical-align: bottom;
            }
        }

        .form-hint {
            font-size: 12px;
            color: #909399;
            line-height: 1.6;
            margin-top: 4px;
        }

        .week-row {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;

            .week-label {
                flex-shrink: 0;
                width: 52px;
                font-size: 13px;
                font-weight: 600;
                color: #606266;
            }

            .date-range-sep {
                flex-shrink: 0;
                color: #909399;
                font-size: 13px;
            }

            .btn-remove-week {
                flex-shrink: 0;
                color: #909399;

                &:hover {
                    color: #F56C6C;
                }
            }
        }

        .attendance-categories {
            display: flex;
            flex-direction: column;
            gap: 14px;
            margin-top: 10px;
        }

        .attendance-category {
            border: 1px solid #EBEEF5;
            border-radius: 8px;
            overflow: hidden;
            background: #FAFAFA;

            &.morning {
                border-top: 3px solid #2563EB;

                .attendance-category-title {
                    color: #2563EB;
                }

                .calendar-day.is-selected {
                    background: #2563EB;
                    border-color: #2563EB;
                }
            }

            &.evening {
                border-top: 3px solid #7C3AED;

                .attendance-category-title {
                    color: #7C3AED;
                }

                .calendar-day.is-selected {
                    background: #7C3AED;
                    border-color: #7C3AED;
                }
            }
        }

        .attendance-category-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: 10px 12px;
            background: #fff;
            border-bottom: 1px solid #EBEEF5;
        }

        .attendance-category-title {
            font-size: 13px;
            font-weight: 600;
        }

        .attendance-category-body {
            padding: 12px;
        }

        .attendance-time-row {
            gap: 10px;
            margin-bottom: 12px;
            flex-wrap: wrap;

            .attendance-time-label {
                font-size: 13px;
                font-weight: 500;
                color: #303133;
                flex-shrink: 0;
            }

            .attendance-time-range {
                gap: 8px;
                flex-wrap: wrap;
            }

            .attendance-time-sep {
                font-size: 13px;
                color: #909399;
            }

            .attendance-selected-count {
                font-size: 12px;
                color: #909399;
                margin-left: auto;
            }
        }

        .attendance-calendar {
            width: 100%;
            background: #fff;
            border: 1px solid #EBEEF5;
            border-radius: 8px;
            padding: 10px 12px;
            box-sizing: border-box;
        }

        .calendar-nav {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 8px;
        }

        .calendar-nav-btn {
            width: 24px;
            height: 24px;
            border: 1px solid #DCDFE6;
            border-radius: 4px;
            background: #fff;
            color: #606266;
            cursor: pointer;
            font-size: 14px;
            line-height: 1;
            padding: 0;

            &:hover:not(:disabled) {
                color: #409EFF;
                border-color: #409EFF;
            }

            &:disabled {
                opacity: 0.4;
                cursor: not-allowed;
            }
        }

        .calendar-title {
            font-size: 13px;
            font-weight: 600;
            color: #303133;
        }

        .calendar-weekdays,
        .calendar-days {
            display: grid;
            grid-template-columns: repeat(7, minmax(0, 1fr));
            gap: 4px;
            width: 100%;
        }

        .calendar-days {
            user-select: none;
            cursor: pointer;

            &.is-dragging {
                cursor: crosshair;
            }
        }

        .calendar-weekday {
            text-align: center;
            font-size: 12px;
            color: #909399;
            padding: 4px 0;
            font-weight: 500;
        }

        .calendar-day {
            width: 100%;
            border: 1px solid transparent;
            border-radius: 4px;
            background: transparent;
            color: #303133;
            font-size: 12px;
            cursor: pointer;
            padding: 0;
            height: 34px;

            &:hover:not(:disabled):not(.is-empty):not(.is-selected) {
                background: #ECF5FF;
                border-color: #B3D8FF;
                color: #303133;
            }

            &.is-selected {
                background: #409EFF;
                color: #fff;
                border-color: #409EFF;

                &:hover:not(:disabled) {
                    color: #fff;
                    filter: brightness(0.92);
                }
            }

            &.is-in-range:not(.is-selected) {
                background: #DBEAFE;
                border-color: #93C5FD;
                color: #1D4ED8;

                &:hover:not(:disabled) {
                    color: #1D4ED8;
                    background: #BFDBFE;
                }
            }

            &.is-deselect-preview {
                background: #FEE2E2;
                border-color: #FCA5A5;
                color: #B91C1C;

                &:hover:not(:disabled) {
                    background: #FECACA;
                    color: #B91C1C;
                    filter: none;
                }
            }

            &.is-disabled,
            &.is-empty {
                color: #C0C4CC;
                cursor: default;
                background: transparent;
            }

            &.is-disabled:not(.is-empty) {
                opacity: 0.45;
            }
        }
    }

    /deep/ .el-dialog__title {
        font-weight: bold;
    }

    /deep/ .el-form-item__label {
        font-weight: bold;
    }
</style>

<style lang="less">
    .stage-manage-dialog {
        .el-dialog__header {
            padding-right: 36px;
        }

        .el-dialog__body {
            padding-right: 36px;
        }

        .el-dialog__footer {
            padding-right: 36px;
        }
    }
</style>
